// ============================================================
// FLUJOS FACTURADOS — R34
// Hoja principal: pedidos del período ACTUAL (nuevos + arrastrados pendientes).
// Historial: pedidos archivados en cierres.
// Orden ascendente. Fotos reales en miniatura clickeables para agrandar.
// Colores de fila:
//   🟩 Verde: facturados 100%
//   ⬜ Blanco: nuevos sin facturar
//   🟪 Rosa: entregados pero pendientes de facturar
//   🟥 Rojo intenso: atrasados (no entregados y vencidos)
//   🟫 Café: anulados
// Export Excel y PDF con coloreado.
// ============================================================
import { useEffect, useState, useMemo } from "react";
import api, { API_BASE, formatApiError, formatMoney, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Loader2, History, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const EST_COLOR = {
  pendiente: "bg-zinc-200 text-zinc-700",
  pendiente_facturar: "bg-zinc-200 text-zinc-700",
  en_proceso: "bg-amber-100 text-amber-900",
  parcial: "bg-yellow-100 text-yellow-900",
  listo: "bg-emerald-100 text-emerald-900",
  entregado: "bg-emerald-100 text-emerald-900",
  facturado: "bg-emerald-100 text-emerald-900",
  pagado: "bg-emerald-100 text-emerald-900",
  cancelado: "bg-red-200 text-red-900",
  falta_pago: "bg-rose-200 text-rose-900",
  pendiente_mats: "bg-amber-100 text-amber-900",
  reprogramado: "bg-teal-100 text-teal-900",
};
const Est = ({ v }) => (
  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${EST_COLOR[v] || "bg-zinc-100 text-zinc-700"}`}>
    {v || "pend."}
  </span>
);

// Resuelve URL absoluta para <img src>. Soporta /api/uploads/..., http(s)://... y base64 data:...
const resolveSrc = (u) => {
  if (!u) return "";
  if (u.startsWith("data:") || u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
};

const Thumb = ({ src, onClick, label, dark, size = 120 }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ width: size, height: size }}
    className={`inline-flex items-center justify-center rounded border overflow-hidden ${dark ? "border-white/60" : "border-zinc-300"} hover:border-blue-500 hover:ring-2 hover:ring-blue-300 transition-shadow bg-white`}
    title={label}
    data-testid={`thumb-${label}`}
  >
    <img src={resolveSrc(src)} alt={label} className="w-full h-full object-cover" loading="lazy" decoding="async" />
  </button>
);

// Clasifica la fila por estado — determina color de fondo
const rowBucket = (p) => {
  if (p.anulado) return "anulado";
  const total = Number(p.total || 0);
  const fact = Number(p.facturado || 0);
  const saldo = Number(p.saldo || 0);
  const entregado = !!p.entregado;
  const fullyBilled = total > 0 && (fact >= total - 0.01 || saldo <= 0.01) && fact > 0;
  if (!fullyBilled && !entregado) {
    const ref = p.fecha_entrega_hasta || p.fecha_entrega || "";
    if (ref) {
      // Compara YYYY-MM-DD de hoy
      const today = new Date().toISOString().slice(0, 10);
      if (ref < today) return "atrasado";
    }
  }
  if (fullyBilled) return "facturado";
  if (entregado && !fullyBilled) return "entregado_pendiente";
  return "nuevo";
};

const BUCKET_CLASSES = {
  anulado:              "bg-amber-900 text-white border-amber-950",
  atrasado:             "bg-red-500 text-white border-red-700",
  facturado:            "bg-green-100 border-green-300",
  entregado_pendiente:  "bg-pink-50 border-pink-200",
  nuevo:                "bg-white border-zinc-300",
};

const BUCKET_LABEL = {
  anulado: "ANULADO",
  atrasado: "ATRASADO",
  facturado: "FACTURADO 100%",
  entregado_pendiente: "ENTREGADO — PENDIENTE FACTURAR",
  nuevo: "NUEVO",
};

export default function FlujoFacturados() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [historial, setHistorial] = useState(false);
  const [photo, setPhoto] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (historial) params.historial = true;
      const { data } = await api.get("/flujos-facturados-v2", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [historial]);

  const totales = useMemo(() => items.reduce((acc, p) => ({
    total: acc.total + Number(p.total || 0),
    pagado: acc.pagado + Number(p.pagado_periodo ?? p.pagado ?? 0),
    facturado: acc.facturado + Number(p.facturado_periodo ?? p.facturado ?? 0),
    saldo: acc.saldo + Number(p.saldo || 0),
  }), { total: 0, pagado: 0, facturado: 0, saldo: 0 }), [items]);

  const exportar = async (fmt) => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (historial) params.set("historial", "true");
      const url = `/flujos-facturados-v2/export.${fmt}?${params.toString()}`;
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `flujos_facturados.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="flujos-facturados-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tight">Flujos Facturados</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {historial ? "Pedidos archivados en cierres" : "Pedidos del período actual (nuevos + arrastrados)"} · <b>{items.length}</b>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => exportar("xlsx")} className="h-10" data-testid="btn-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
          </Button>
          <Button variant="outline" onClick={() => exportar("pdf")} className="h-10" data-testid="btn-export-pdf">
            <FileText className="w-4 h-4 mr-2" />PDF
          </Button>
          <Button variant={historial ? "default" : "outline"} onClick={() => setHistorial((v) => !v)} data-testid="toggle-historial" className="h-10">
            <History className="w-4 h-4 mr-2" />{historial ? "Viendo historial" : "Ver historial"}
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input placeholder="Buscar por número, cliente o teléfono..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9 h-10" data-testid="search-input" />
        </div>
        <Button onClick={load} className="h-10" data-testid="btn-search">Buscar</Button>
      </div>

      {/* Leyenda de colores */}
      <div className="flex flex-wrap gap-2 text-[10px] font-bold">
        <span className="px-2 py-1 rounded bg-green-100 border border-green-300">VERDE · Facturado 100%</span>
        <span className="px-2 py-1 rounded bg-white border border-zinc-300">BLANCO · Nuevo</span>
        <span className="px-2 py-1 rounded bg-pink-50 border border-pink-200">ROSA · Entregado pend. facturar</span>
        <span className="px-2 py-1 rounded bg-red-500 text-white border border-red-700">ROJO · Atrasado</span>
        <span className="px-2 py-1 rounded bg-amber-900 text-white border border-amber-950">CAFÉ · Anulado</span>
      </div>

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
          <div className="bg-indigo-50 border border-indigo-200 rounded p-2"><div className="text-[9px] uppercase font-bold text-indigo-700">Total</div><div className="font-black tabular-nums text-indigo-900">{formatMoney(totales.total)}</div></div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2"><div className="text-[9px] uppercase font-bold text-emerald-700">Pagado</div><div className="font-black tabular-nums text-emerald-900">{formatMoney(totales.pagado)}</div></div>
          <div className="bg-blue-50 border border-blue-200 rounded p-2"><div className="text-[9px] uppercase font-bold text-blue-700">Facturado</div><div className="font-black tabular-nums text-blue-900">{formatMoney(totales.facturado)}</div></div>
          <div className="bg-amber-50 border border-amber-200 rounded p-2"><div className="text-[9px] uppercase font-bold text-amber-700">Saldo</div><div className="font-black tabular-nums text-amber-900">{formatMoney(totales.saldo)}</div></div>
        </div>
      )}

      {loading && <div className="flex items-center justify-center gap-2 text-zinc-600 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>}

      {/* Lista */}
      {!loading && items.map((p) => {
        const bucket = rowBucket(p);
        const bucketCls = BUCKET_CLASSES[bucket];
        const dark = bucket === "anulado" || bucket === "atrasado";
        const mutedCls = dark ? "text-white/80" : "text-zinc-500";
        const subCls = dark ? "text-white/90" : "text-zinc-600";
        return (
          <div key={p.id} className={`${bucketCls} border rounded-lg p-2.5 shadow-sm hover:shadow-md transition-shadow`} data-testid={`pedido-${p.numero}`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-[9px] font-black uppercase tracking-wider ${mutedCls}`}>{BUCKET_LABEL[bucket]}</span>
              {p.cierre_id && !historial && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${dark ? "bg-white/20" : "bg-zinc-200 text-zinc-700"}`}>ARRASTRADO</span>
              )}
            </div>
            <div className="grid grid-cols-12 gap-2 items-start text-xs">
              {/* Col 1: Pedido / Fechas / Ciudad-Dept / Guía */}
              <div className="col-span-2">
                <div className={`font-black text-sm ${dark ? "text-white" : "text-blue-900"}`}>{p.numero}</div>
                <div className={`text-[10px] ${subCls}`}>Creado: <b>{formatDate(p.fecha)}</b></div>
                <div className={`text-[10px] ${subCls}`}>Entrega: <b>{p.fecha_entrega || "—"}</b></div>
                <div className="mt-1 flex flex-wrap gap-1">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${p.envio_tipo === "ciudad" ? "bg-sky-100 text-sky-800" : "bg-purple-100 text-purple-800"}`}>{p.envio_tipo || "—"}</span>
                  {p.numero_guia ? (
                    <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-zinc-800 text-white">Guía: {p.numero_guia}</span>
                  ) : (
                    <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-zinc-200 text-zinc-700">Sin guía</span>
                  )}
                </div>
              </div>

              {/* Col 2: Vendedora + Producto */}
              <div className="col-span-3">
                <div className={`text-[9px] uppercase font-bold ${mutedCls}`}>Vendedora</div>
                <div className="font-bold truncate">{p.vendedor}</div>
                <div className={`text-[9px] uppercase font-bold ${mutedCls} mt-1`}>Producto</div>
                <ul className="space-y-0">
                  {p.productos.map((pr, i) => <li key={i} className="text-[11px] truncate">• {pr}</li>)}
                </ul>
              </div>

              {/* Col 3: Cliente + Dirección */}
              <div className="col-span-3">
                <div className={`text-[9px] uppercase font-bold ${mutedCls}`}>Cliente</div>
                <div className="font-bold truncate">{p.cliente_nombre}</div>
                {p.cliente_telefono && <div className={`text-[10px] ${subCls}`}>{p.cliente_telefono}</div>}
                {p.direccion && <div className={`text-[10px] ${mutedCls} line-clamp-2`}>{p.direccion}</div>}
              </div>

              {/* Col 4: Estados compactos sin fotos */}
              <div className="col-span-3">
                <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                  <div className="flex items-center gap-1"><span className={`text-[9px] uppercase font-bold ${mutedCls} w-10`}>Fab:</span><Est v={p.estado_fabricacion} /></div>
                  <div className="flex items-center gap-1"><span className={`text-[9px] uppercase font-bold ${mutedCls} w-10`}>Emp:</span><Est v={p.estado_empaque} /></div>
                  <div className="flex items-center gap-1"><span className={`text-[9px] uppercase font-bold ${mutedCls} w-10`}>Ent:</span>{p.entregado ? <Est v="entregado" /> : <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-700 text-white uppercase">NO ENT.</span>}</div>
                  <div className="flex items-center gap-1"><span className={`text-[9px] uppercase font-bold ${mutedCls} w-10`}>Pago:</span><Est v={p.estado} /></div>
                  <div className="flex items-center gap-1 col-span-2"><span className={`text-[9px] uppercase font-bold ${mutedCls} w-10`}>Fact:</span><Est v={p.estado_facturacion} /></div>
                </div>
              </div>

              {/* Col 5: Financiero */}
              <div className="col-span-1 text-right">
                <div className="grid grid-cols-1 gap-0.5">
                  <div><span className={`text-[9px] uppercase font-bold mr-1 ${dark ? "text-white/80" : "text-indigo-700"}`}>Total:</span><span className={`font-black tabular-nums ${dark ? "text-white" : "text-indigo-900"}`}>{formatMoney(p.total)}</span></div>
                  <div><span className={`text-[9px] uppercase font-bold mr-1 ${dark ? "text-white/80" : "text-blue-700"}`}>Fact:</span><span className={`font-black tabular-nums ${dark ? "text-white" : "text-blue-900"}`}>{formatMoney(p.facturado)}</span></div>
                  <div><span className={`text-[9px] uppercase font-bold mr-1 ${dark ? "text-white/80" : "text-amber-700"}`}>Saldo:</span><span className={`font-black tabular-nums ${dark ? "text-white" : (p.saldo > 0 ? "text-amber-900" : "text-emerald-900")}`}>{formatMoney(p.saldo)}</span></div>
                </div>
              </div>
            </div>

            {/* Fotos abiertas 120×120 con lazy loading, agrupadas por tipo */}
            {((p.fabricacion_fotos_urls || []).length > 0 || p.entrega_foto_url || (p.facturas_fotos || []).length > 0 || (p.domicilio_fotos_urls || []).length > 0) && (
              <div className={`mt-2 pt-2 border-t ${dark ? "border-white/30" : "border-zinc-200"} space-y-2`}>
                {(p.fabricacion_fotos_urls || []).length > 0 && (
                  <div>
                    <div className={`text-[9px] uppercase font-bold mb-1 ${mutedCls}`}>Fabricación · {(p.fabricacion_fotos_urls || []).length}</div>
                    <div className="flex flex-wrap gap-2">
                      {(p.fabricacion_fotos_urls || []).map((src, i) => (
                        <Thumb key={`f${i}`} src={src} dark={dark} label={`${p.numero} · Fabricación ${i + 1}`} onClick={() => setPhoto({ src: resolveSrc(src), title: `${p.numero} · Fabricación ${i + 1}` })} />
                      ))}
                    </div>
                  </div>
                )}
                {p.entrega_foto_url && (
                  <div>
                    <div className={`text-[9px] uppercase font-bold mb-1 ${mutedCls}`}>Entrega</div>
                    <div className="flex flex-wrap gap-2">
                      <Thumb src={p.entrega_foto_url} dark={dark} label={`${p.numero} · Entrega`} onClick={() => setPhoto({ src: resolveSrc(p.entrega_foto_url), title: `${p.numero} · Entrega` })} />
                    </div>
                  </div>
                )}
                {(p.facturas_fotos || []).length > 0 && (
                  <div>
                    <div className={`text-[9px] uppercase font-bold mb-1 ${mutedCls}`}>Boletas de facturas · {(p.facturas_fotos || []).length}</div>
                    <div className="flex flex-wrap gap-2">
                      {(p.facturas_fotos || []).map((ff, i) => (
                        <div key={`ff${i}`} className="flex flex-col items-center">
                          <Thumb src={ff.src} dark={dark} label={`${p.numero} · F ${ff.numero || i + 1}`} onClick={() => setPhoto({ src: resolveSrc(ff.src), title: `${p.numero} · Factura ${ff.numero || i + 1}` })} />
                          {ff.numero && <span className={`text-[9px] mt-0.5 ${mutedCls}`}>F {ff.numero}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(p.domicilio_fotos_urls || []).length > 0 && (
                  <div>
                    <div className={`text-[9px] uppercase font-bold mb-1 ${mutedCls}`}>Comprobantes de domicilio · {(p.domicilio_fotos_urls || []).length}</div>
                    <div className="flex flex-wrap gap-2">
                      {(p.domicilio_fotos_urls || []).map((src, i) => (
                        <Thumb key={`d${i}`} src={src} dark={dark} label={`${p.numero} · Dom ${i + 1}`} onClick={() => setPhoto({ src: resolveSrc(src), title: `${p.numero} · Comprobante domicilio ${i + 1}` })} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Forma de pago desde facturas (si existe) */}
            {p.formas_pago.length > 0 && (
              <div className={`mt-2 pt-2 border-t flex flex-wrap gap-x-3 gap-y-1 text-[10px] ${dark ? "border-white/30" : "border-zinc-200"}`}>
                {p.formas_pago.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    <b>F{f.numero_factura || i + 1}</b>
                    <span className={`font-bold ${dark ? "text-white" : "text-emerald-700"}`}>{formatMoney(f.monto)}</span>
                    {f.es_anticipo && <span className="bg-emerald-700 text-white px-1 rounded text-[8px]">Ant.</span>}
                    {f.metodo_pago && <span>· {f.metodo_pago}</span>}
                    {f.banco && <span>· {f.banco}</span>}
                    {f.numero_cuenta && <span>· Cta {f.numero_cuenta}</span>}
                    {f.referencia && <span className={dark ? "text-white/80" : "text-zinc-500"}>· Ref {f.referencia}</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {!loading && items.length === 0 && (
        <div className="text-center text-zinc-500 py-10 border-2 border-dashed border-zinc-200 rounded-xl">
          No hay pedidos para mostrar.
        </div>
      )}

      <Dialog open={!!photo} onOpenChange={(v) => !v && setPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{photo?.title}</DialogTitle></DialogHeader>
          {photo && (
            <div className="w-full max-h-[80vh] overflow-auto flex items-center justify-center bg-zinc-50 rounded-lg">
              <img src={photo.src} alt={photo.title} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
